import { C, DOC_INFO, EMPRESA, ESTADOS } from "../core/config.js";
import { aplicarLogoEmpresa, saveEmpresaConfig } from "../core/cloudSync.js";
import { state } from "../core/state.js";
import { btnStyle, escapeHTML, inputStyle } from "../core/utils.js";
import { registerActions } from "../ui/actions.js";
import { FORMATO_DOC_DEFAULTS, IMAGEN_CT_MAX, IMAGEN_CT_MIN, getFormatoDoc, saveFormatoDoc } from "../core/formatoDoc.js";
import { SPLASH_DEFAULTS, abrirSplash, getSplashConfig, saveSplashConfig } from "../ui/splash.js";
import { render } from "../ui/app.js";
import { docTieneHijos } from "./documentos.js";
import { getVencimientoMatricula } from "./alertas.js";

// Sube el logo de la empresa: se conserva en alta resolución (hasta 800px de
// alto y 1600px de ancho, respetando la proporción) para que se vea grande,
// representativo y nítido incluso en pantallas de alta densidad. Solo se
// reescala si la imagen original supera esos límites; PNG sin pérdida para no
// degradar la calidad. Se aplica de inmediato en toda la app (login, dashboard,
// encabezados y documentos impresos).
export function handleLogoEmpresaChange(e) {
  const file = e.target.files && e.target.files[0];
  if (!file) return;
  if (!file.type.startsWith("image/")) { alert("⚠️ Selecciona un archivo de imagen válido (PNG o JPG)."); return; }
  if (file.size > 5 * 1024 * 1024) { alert("⚠️ La imagen es muy grande. Máximo 5MB."); return; }
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      // Límites generosos: solo se reduce si la imagen es enorme. La proporción
      // se conserva tomando el factor de escala más restrictivo entre alto y ancho.
      const MAX_ALTO = 800;
      const MAX_ANCHO = 1600;
      const escala = Math.min(1, MAX_ALTO / img.height, MAX_ANCHO / img.width);
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * escala);
      canvas.height = Math.round(img.height * escala);
      const ctx = canvas.getContext("2d");
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      EMPRESA.logo = canvas.toDataURL("image/png");
      saveEmpresaConfig();
      aplicarLogoEmpresa();
      render();
    };
    img.onerror = () => alert("⚠️ No se pudo leer la imagen. Intenta con otro archivo.");
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
}

registerActions({
  "quitar-logo-empresa": () => {
    if (!confirm("¿Quitar el logo? La app volverá a mostrar el recuadro \"Tu logo\" hasta que subas uno nuevo.")) return;
    delete EMPRESA.logo;
    saveEmpresaConfig();
    aplicarLogoEmpresa();
    render();
  }
});

// ─── Pantalla de bienvenida (solo administradores) ──────────────────────────
// Los deslizadores escriben en erp:splash_config, que se sincroniza a la nube
// como cualquier configuración. La vista previa abre la MISMA pantalla real
// (no una maqueta), así lo que se ajusta es exactamente lo que se verá.
const CAMPOS_BIENVENIDA = [
  { k:"segundos",     label:"Duración en pantalla (s)", min:2,   max:15,  step:1 },
  { k:"count",        label:"Hebras totales",           min:2,   max:10,  step:1 },
  { k:"detras",       label:"· de ellas, detrás",       min:0,   max:10,  step:1 },
  { k:"brilloFrente", label:"Brillo del frente",        min:0,   max:1.4, step:0.05 },
  { k:"speed",        label:"Velocidad",                min:0,   max:1.5, step:0.05 },
  { k:"amplitude",    label:"Amplitud",                 min:0,   max:3,   step:0.1 },
  { k:"waviness",     label:"Ondulación",               min:0,   max:3,   step:0.1 },
  { k:"thickness",    label:"Grosor",                   min:0.1, max:2,   step:0.05 },
  { k:"glow",         label:"Resplandor",               min:0.5, max:6,   step:0.1 },
  { k:"taper",        label:"Difuminado",               min:0.5, max:6,   step:0.1 },
  { k:"spread",       label:"Separación",               min:0,   max:3,   step:0.1 },
  { k:"intensity",    label:"Intensidad",               min:0,   max:1,   step:0.05 },
  { k:"saturation",   label:"Saturación",               min:0,   max:2.5, step:0.05 },
  { k:"scale",        label:"Escala",                   min:0.3, max:3,   step:0.05 },
  { k:"offset",       label:"Altura de la banda",       min:-0.25, max:0.25, step:0.01 }
];

function seccionBienvenida() {
  const cfg = getSplashConfig();
  const filas = CAMPOS_BIENVENIDA.map(c => `
    <div style="display:flex;align-items:center;gap:9px;">
      <label style="font-size:10.5px;color:${C.muted};font-weight:600;flex:1;min-width:0;">${c.label}</label>
      <input type="range" min="${c.min}" max="${c.max}" step="${c.step}" value="${cfg[c.k]}"
        data-splash="${c.k}" style="width:120px;accent-color:${C.gold};cursor:pointer;"
        oninput="ajustarBienvenida('${c.k}', this.value, this)"/>
      <b id="__sv_${c.k}" style="font-size:11px;color:${C.mathiz};min-width:34px;text-align:right;">${cfg[c.k]}</b>
    </div>`).join("");

  return `
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:11px;margin-bottom:10px;">
      <h3 style="margin:0 0 6px;font-size:12px;color:${C.text};font-weight:700;">✨ PANTALLA DE BIENVENIDA</h3>
      <p style="font-size:11.5px;color:${C.muted};margin:0 0 9px;">
        La animación que se muestra tras iniciar sesión mientras carga el ERP. Los cambios se guardan
        solos y aplican para toda la empresa. Usa <b>Ver a pantalla completa</b> para probarlos en vivo:
        puedes seguir moviendo los controles con la vista previa abierta.
      </p>
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(270px,1fr));gap:6px 18px;">
        ${filas}
      </div>
      <div style="display:flex;gap:6px;flex-wrap:wrap;margin-top:11px;">
        <button data-action="bienvenida-previsualizar" style="${btnStyle(C.mathiz)}">▶ Ver a pantalla completa</button>
        <button data-action="bienvenida-restaurar" style="${btnStyle(null,"secondary")}">↺ Restaurar valores por defecto</button>
      </div>
    </div>
  `;
}

// ─── Formato de documentos (solo administradores) ───────────────────────────
// Ajustes de cómo se ve un documento impreso. Por ahora, el tamaño de la
// miniatura del producto en la cotización: en catálogos de promocionales la
// foto vende, y cada empresa quiere darle más o menos peso frente a la ficha.
function seccionFormatoDoc() {
  const cfg = getFormatoDoc();
  return `
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:11px;margin-bottom:10px;">
      <h3 style="margin:0 0 6px;font-size:12px;color:${C.text};font-weight:700;">🖨️ FORMATO DE DOCUMENTOS</h3>
      <p style="font-size:11.5px;color:${C.muted};margin:0 0 9px;">
        Cómo se ven los documentos al imprimirlos. Los cambios se guardan solos y aplican para toda la empresa.
      </p>
      <div style="display:flex;align-items:center;gap:11px;flex-wrap:wrap;">
        <label style="font-size:10.5px;color:${C.muted};font-weight:600;">Tamaño de la imagen del producto en la cotización</label>
        <input type="range" min="${IMAGEN_CT_MIN}" max="${IMAGEN_CT_MAX}" step="5" value="${cfg.imagenCT}"
          style="width:170px;accent-color:${C.gold};cursor:pointer;"
          oninput="ajustarFormatoDoc('imagenCT', this.value)"/>
        <b id="__fd_imagenCT" style="font-size:11px;color:${C.mathiz};min-width:78px;">${cfg.imagenCT} px · ${(cfg.imagenCT / 37.8).toFixed(1)} cm</b>
      </div>
      <div style="font-size:10px;color:${C.muted};margin-top:7px;">
        💡 La medida en centímetros es la del papel impreso. Imprime una cotización con productos
        que tengan foto para ver el resultado; si la subes mucho, la descripción pierde espacio.
      </div>
      <div style="margin-top:10px;">
        <button data-action="formato-doc-restaurar" style="${btnStyle(null,"secondary")}">↺ Restaurar valor por defecto</button>
      </div>
    </div>
  `;
}

// Igual que los deslizadores de la bienvenida: se guarda sin re-renderizar,
// porque un render mataría el arrastre del control.
window.ajustarFormatoDoc = function (clave, valor) {
  const cfg = saveFormatoDoc({ ...getFormatoDoc(), [clave]: Number(valor) });
  const salida = document.getElementById("__fd_" + clave);
  if (salida) salida.textContent = `${cfg[clave]} px · ${(cfg[clave] / 37.8).toFixed(1)} cm`;
};

registerActions({
  "formato-doc-restaurar": () => {
    saveFormatoDoc({ ...FORMATO_DOC_DEFAULTS });
    render();
  },
  "bienvenida-previsualizar": () => {
    abrirSplash({
      nombre: state.usuarioNombre || state.usuarioEmail,
      rolLabel: "Vista previa",
      rolIcon: "👁️",
      empresa: EMPRESA.nombre || "",
      demo: true
    });
  },
  "bienvenida-restaurar": () => {
    if (!confirm("¿Restaurar la pantalla de bienvenida a sus valores por defecto?")) return;
    saveSplashConfig({ ...SPLASH_DEFAULTS });
    render();
  }
});

// Los deslizadores llaman aquí en cada movimiento: se guarda sin re-renderizar
// (un render mataría el arrastre del control) y la vista previa, si está
// abierta, lo refleja al instante porque relee la configuración cada cuadro.
window.ajustarBienvenida = function (clave, valor, el) {
  const cfg = { ...getSplashConfig(), [clave]: parseFloat(valor) };
  if (clave === "count" && cfg.detras > cfg.count) cfg.detras = cfg.count;
  if (clave === "detras" && cfg.detras > cfg.count) cfg.detras = cfg.count;
  saveSplashConfig(cfg);
  const salida = document.getElementById("__sv_" + clave);
  if (salida) salida.textContent = cfg[clave];
  if (el && clave === "detras" && cfg.detras !== parseFloat(valor)) el.value = cfg.detras;
};

export function renderConfiguracion() {
  const esAdmin = !state.usuarioRol || state.usuarioRol === "admin";
  const vencimientoMatricula = getVencimientoMatricula();

  // Casilla del RUT que describe a la propia empresa. Lleva el código impreso
  // para poder cotejarla de un vistazo con el documento.
  const respEmpresa = (key, label, codigo) => `
    <label style="display:flex;align-items:center;gap:6px;font-size:11.5px;color:${C.text};cursor:pointer;">
      <input type="checkbox" ${EMPRESA[key]?"checked":""} onchange="EMPRESA['${key}']=this.checked;saveEmpresaConfig();render();"/>
      ${label} <span style="color:${C.muted};font-size:10px;">(${codigo})</span>
    </label>`;

  const stats = [
    { label:"Clientes", count:state.data.clientes.length, icon:"👥" },
    { label:"Proveedores", count:state.data.proveedores.length, icon:"🏭" },
    { label:"Productos", count:state.data.productos.length, icon:"📦" },
    { label:"Cotizaciones", count:state.data.cotizaciones.length, icon:"📋" },
    { label:"Órd. Producción", count:state.data.pedidos.length, icon:"🔧" },
    { label:"Órd. Compra", count:state.data.ocp.length, icon:"🛒" },
    { label:"Remisiones", count:state.data.remisiones.length, icon:"📮" }
  ];

  const totalDocumentos = stats.reduce((sum, s) => sum + s.count, 0);

  const statsHTML = stats.map(s => `
    <div style="padding:9px;background:#f8fafc;border-radius:6px;text-align:center;border-left:3px solid ${C.accent};">
      <div style="font-size:17px;margin-bottom:4px;">${s.icon}</div>
      <div style="font-size:17px;font-weight:800;color:${C.mathiz};">${s.count}</div>
      <div style="font-size:10.5px;color:${C.muted};font-weight:600;">${s.label}</div>
    </div>
  `).join("");

  return `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:9px;padding-bottom:10px;border-bottom:3px solid ${C.gold};">
      <div>
        <h2 style="margin:0;font-size:16px;color:${C.text};font-weight:700;">⚙️ Configuración del Sistema</h2>
        <p style="margin:3px 0 0;color:${C.muted};font-size:11.5px;">Panel de administración y opciones avanzadas</p>
      </div>
    </div>

    <!-- INFORMACIÓN DEL SISTEMA -->
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:11px;margin-bottom:10px;">
      <h3 style="margin:0 0 9px;font-size:12px;color:${C.text};font-weight:700;">📊 INFORMACIÓN DEL SISTEMA</h3>
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:10px;margin-bottom:10px;">
        <div>
          <div style="font-size:10.5px;color:${C.muted};font-weight:700;margin-bottom:4px;">EMPRESA</div>
          <div style="font-size:14px;font-weight:700;color:${C.text};">${EMPRESA.nombre ? escapeHTML(EMPRESA.nombre) : `<span style="color:${C.red};">Sin configurar</span>`}</div>
          <div style="font-size:10.5px;color:${C.muted};">${EMPRESA.nit ? `NIT ${escapeHTML(EMPRESA.nit)}` : "Diligencia los datos en la sección de abajo ↓"}</div>
        </div>
        <div>
          <div style="font-size:10.5px;color:${C.muted};font-weight:700;margin-bottom:4px;">VERSIÓN</div>
          <div style="font-size:14px;font-weight:700;color:${C.text};">3.1 Trazabilidad</div>
          <div style="font-size:10.5px;color:#10b981;font-weight:700;">✅ Actual</div>
        </div>
        <div>
          <div style="font-size:10.5px;color:${C.muted};font-weight:700;margin-bottom:4px;">ALMACENAMIENTO</div>
          <div style="font-size:14px;font-weight:700;color:${C.text};">☁️ Nube (Supabase)</div>
          <div style="font-size:10.5px;color:${C.muted};">Sincronizado en tiempo real · caché local en el navegador</div>
        </div>
        <div>
          <div style="font-size:10.5px;color:${C.muted};font-weight:700;margin-bottom:4px;">DOCUMENTOS TOTALES</div>
          <div style="font-size:14px;font-weight:700;color:${C.mathiz};">${totalDocumentos}</div>
          <div style="font-size:10.5px;color:${C.muted};">registros</div>
        </div>
      </div>
    </div>

    <!-- DATOS DE LA EMPRESA -->
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:11px;margin-bottom:10px;">
      <h3 style="margin:0 0 6px;font-size:12px;color:${C.text};font-weight:700;">🏢 DATOS DE LA EMPRESA</h3>
      <p style="font-size:11.5px;color:${C.muted};margin:0 0 9px;">Aparecen en el encabezado de todos los documentos impresos (cotizaciones, facturas, remisiones). Se guardan automáticamente a medida que escribes.</p>
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:9px;">
        <div style="grid-column:1 / -1;">
          <label style="font-size:10px;color:${C.muted};font-weight:600;display:block;margin-bottom:3px;">Razón social *</label>
          <input value="${escapeHTML(EMPRESA.nombre||"")}" placeholder="Ej: MI EMPRESA S.A.S." style="${inputStyle}" oninput="EMPRESA.nombre=this.value;saveEmpresaConfig();" onchange="EMPRESA.nombre=this.value.trim();saveEmpresaConfig();render();"/>
        </div>
        <div>
          <label style="font-size:10px;color:${C.muted};font-weight:600;display:block;margin-bottom:3px;">NIT *</label>
          <input value="${escapeHTML(EMPRESA.nit||"")}" placeholder="Ej: 900.123.456-7" style="${inputStyle}" oninput="EMPRESA.nit=this.value;saveEmpresaConfig();" onchange="EMPRESA.nit=this.value.trim();saveEmpresaConfig();render();"/>
        </div>
        <div>
          <label style="font-size:10px;color:${C.muted};font-weight:600;display:block;margin-bottom:3px;">Tipo de persona</label>
          <select style="${inputStyle}cursor:pointer;" onchange="EMPRESA.tipo_persona=this.value;saveEmpresaConfig();render();">
            <option value="Jurídica" ${EMPRESA.tipo_persona==="Jurídica"?"selected":""}>Jurídica</option>
            <option value="Natural" ${EMPRESA.tipo_persona==="Natural"?"selected":""}>Natural</option>
          </select>
        </div>
        <div>
          <label style="font-size:10px;color:${C.muted};font-weight:600;display:block;margin-bottom:3px;">Dirección</label>
          <input value="${escapeHTML(EMPRESA.direccion||"")}" placeholder="Ej: Calle 10 # 20-30, Bogotá" style="${inputStyle}" oninput="EMPRESA.direccion=this.value;saveEmpresaConfig();" onchange="EMPRESA.direccion=this.value.trim();saveEmpresaConfig();"/>
        </div>
        <div>
          <label style="font-size:10px;color:${C.muted};font-weight:600;display:block;margin-bottom:3px;">Teléfono</label>
          <input value="${escapeHTML(EMPRESA.telefono||"")}" placeholder="Ej: 3001234567" style="${inputStyle}" oninput="EMPRESA.telefono=this.value;saveEmpresaConfig();" onchange="EMPRESA.telefono=this.value.trim();saveEmpresaConfig();"/>
        </div>
        <div>
          <label style="font-size:10px;color:${C.muted};font-weight:600;display:block;margin-bottom:3px;">Correo electrónico</label>
          <input value="${escapeHTML(EMPRESA.email||"")}" placeholder="Ej: contacto@miempresa.com" style="${inputStyle}" oninput="EMPRESA.email=this.value;saveEmpresaConfig();" onchange="EMPRESA.email=this.value.trim();saveEmpresaConfig();"/>
        </div>
      </div>
      ${!EMPRESA.nombre || !EMPRESA.nit ? `<div style="margin-top:12px;padding:7px 10px;background:#fffbeb;border:1px solid #fcd34d;border-radius:6px;font-size:11.5px;color:#b45309;font-weight:600;">⚠️ La razón social y el NIT son obligatorios: los documentos impresos saldrán sin identificación de la empresa hasta que los diligencies. Este es el paso 1 de la Puesta en Marcha.</div>` : ""}
    </div>

    <!-- LOGO DE LA EMPRESA -->
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:11px;margin-bottom:10px;">
      <h3 style="margin:0 0 12px;font-size:12px;color:${C.text};font-weight:700;">🖼️ LOGO DE LA EMPRESA</h3>
      <div style="display:flex;gap:20px;align-items:center;flex-wrap:wrap;">
        <div style="padding:20px 28px;background:#f8fafc;border:1px dashed ${C.border};border-radius:6px;display:flex;align-items:center;justify-content:center;min-height:140px;">
          <img src="${window.LOGO}" alt="Logo" style="height:120px;max-width:420px;object-fit:contain;display:block;"/>
        </div>
        <div style="flex:1;min-width:260px;">
          <p style="font-size:11.5px;color:${C.muted};margin:0 0 10px;line-height:1.6;">
            Aparece en la pantalla de inicio, el dashboard, los encabezados de cada módulo y en todos los documentos impresos (cotizaciones, facturas, remisiones). Se conserva en alta resolución para que se vea grande y nítido. PNG con fondo transparente se ve mejor. Máximo 5MB — se ajusta automáticamente solo si es muy grande.
          </p>
          <div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap;">
            <input type="file" accept="image/*" onchange="handleLogoEmpresaChange(event)" style="font-size:12px;"/>
            ${EMPRESA.logo ? `<button data-action="quitar-logo-empresa" style="${btnStyle(null,"secondary",true)}">🗑️ Quitar logo</button>` : `<span style="font-size:10.5px;color:${C.muted};">Aún no has subido el logo de tu empresa</span>`}
          </div>
        </div>
      </div>
    </div>

    <!-- RS ESCANEADAS (ONEDRIVE) -->
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:11px;margin-bottom:10px;">
      <h3 style="margin:0 0 6px;font-size:12px;color:${C.text};font-weight:700;">📎 RS ESCANEADAS (ONEDRIVE)</h3>
      <p style="font-size:11.5px;color:${C.muted};margin:0 0 9px;line-height:1.6;">
        Enlace <b>público</b> a la carpeta de OneDrive donde guardas las remisiones escaneadas.
        Desde el <b>control de remisiones sin facturar</b>, el botón 📎 de cada remisión abre esta carpeta
        y copia al portapapeles el nombre del archivo (número + tercero) para que lo busques rápido.
        Organiza los escaneos como <code>rs por facturar / (asesor) / (fecha)</code> y nombra cada archivo con el número de la remisión y el nombre del tercero.
      </p>
      <input value="${escapeHTML(EMPRESA.rsEscaneadasUrl||"")}" placeholder="https://1drv.ms/f/s!... o enlace público de la carpeta"
        style="${inputStyle}" oninput="EMPRESA.rsEscaneadasUrl=this.value;saveEmpresaConfig();" onchange="EMPRESA.rsEscaneadasUrl=this.value.trim();saveEmpresaConfig();render();"/>
      ${EMPRESA.rsEscaneadasUrl ? `<div style="margin-top:6px;"><a href="${escapeHTML(EMPRESA.rsEscaneadasUrl)}" target="_blank" rel="noopener" style="font-size:11px;color:#0E7490;font-weight:700;text-decoration:none;">🔗 Abrir la carpeta para verificar el enlace</a></div>` : ""}
    </div>

    <!-- ESTADÍSTICAS DE DOCUMENTOS -->
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:11px;margin-bottom:10px;">
      <h3 style="margin:0 0 9px;font-size:12px;color:${C.text};font-weight:700;">🧾 DATOS TRIBUTARIOS DE LA EMPRESA (DIAN)</h3>
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:9px;">
        <div>
          <label style="font-size:10px;color:${C.muted};font-weight:600;display:block;margin-bottom:3px;">Régimen de IVA</label>
          <select style="${inputStyle}cursor:pointer;" onchange="EMPRESA.regimen_iva=this.value;saveEmpresaConfig();render();">
            <option value="responsable" ${EMPRESA.regimen_iva==="responsable"?"selected":""}>Responsable de IVA</option>
            <option value="no_responsable" ${EMPRESA.regimen_iva==="no_responsable"?"selected":""}>No responsable de IVA</option>
          </select>
        </div>
        <div>
          <label style="font-size:10px;color:${C.muted};font-weight:600;display:block;margin-bottom:3px;">Dígito de verificación (DV)</label>
          <input value="${escapeHTML(EMPRESA.dv||"")}" maxlength="1" style="${inputStyle}text-align:right;" oninput="EMPRESA.dv=this.value;saveEmpresaConfig();" onchange="EMPRESA.dv=this.value;saveEmpresaConfig();"/>
        </div>
        <div>
          <label style="font-size:10px;color:${C.muted};font-weight:600;display:block;margin-bottom:3px;">Cód. actividad económica (CIIU)</label>
          <input value="${escapeHTML(EMPRESA.actividad_economica||"")}" style="${inputStyle}" oninput="EMPRESA.actividad_economica=this.value;saveEmpresaConfig();" onchange="EMPRESA.actividad_economica=this.value;saveEmpresaConfig();"/>
        </div>
        <div>
          <label style="font-size:10px;color:${C.muted};font-weight:600;display:block;margin-bottom:3px;">Ciudad / Municipio (DIAN)</label>
          <input value="${escapeHTML(EMPRESA.ciudad_dian||"")}" style="${inputStyle}" oninput="EMPRESA.ciudad_dian=this.value;saveEmpresaConfig();" onchange="EMPRESA.ciudad_dian=this.value;saveEmpresaConfig();"/>
        </div>
      </div>
      <div style="display:flex;gap:10px 16px;flex-wrap:wrap;margin-top:12px;">
        ${respEmpresa("gran_contribuyente","Gran contribuyente","13")}
        ${respEmpresa("autorretenedor","Autorretenedor","15")}
        ${respEmpresa("regimen_simple","Régimen Simple (RST)","47")}
        ${respEmpresa("agente_retencion_renta","Agente de retención (renta)","07")}
        ${respEmpresa("agente_retencion_iva","Agente de retención de IVA","09")}
        ${respEmpresa("obligado_contabilidad","Obligado a llevar contabilidad","42")}
        ${respEmpresa("facturador_electronico","Facturador electrónico","52")}
      </div>
      ${(EMPRESA.responsabilidades||[]).length ? `
        <div style="font-size:10px;color:${C.muted};margin-top:8px;">
          Casilla 53 del RUT leída: <b style="color:${C.text};">${escapeHTML((EMPRESA.responsabilidades||[]).join(" · "))}</b>
        </div>` : ""}
      <div style="font-size:10px;color:${C.muted};margin-top:8px;">💡 Si la empresa NO es responsable de IVA, el IVA pagado en compras se suma automáticamente al costo del inventario (no es descontable).</div>
    </div>

    <!-- REGISTRO MERCANTIL -->
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:11px;margin-bottom:10px;">
      <h3 style="margin:0 0 9px;font-size:12px;color:${C.text};font-weight:700;">🏛️ REGISTRO MERCANTIL (CÁMARA DE COMERCIO)</h3>
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:9px;">
        <div>
          <label style="font-size:10px;color:${C.muted};font-weight:600;display:block;margin-bottom:3px;">Matrícula mercantil</label>
          <input value="${escapeHTML(EMPRESA.matricula_mercantil||"")}" style="${inputStyle}" oninput="EMPRESA.matricula_mercantil=this.value;saveEmpresaConfig();" onchange="EMPRESA.matricula_mercantil=this.value.trim();saveEmpresaConfig();render();"/>
        </div>
        <div>
          <label style="font-size:10px;color:${C.muted};font-weight:600;display:block;margin-bottom:3px;">Último año renovado</label>
          <input value="${escapeHTML(EMPRESA.matricula_anio_renovado||"")}" placeholder="Ej: ${new Date().getFullYear()}" maxlength="4" style="${inputStyle}text-align:right;" oninput="EMPRESA.matricula_anio_renovado=this.value.replace(/\\D/g,'');saveEmpresaConfig();" onchange="EMPRESA.matricula_anio_renovado=this.value.replace(/\\D/g,'');saveEmpresaConfig();render();"/>
        </div>
        <div>
          <label style="font-size:10px;color:${C.muted};font-weight:600;display:block;margin-bottom:3px;">Representante legal</label>
          <input value="${escapeHTML(EMPRESA.representante_legal||"")}" style="${inputStyle}" oninput="EMPRESA.representante_legal=this.value;saveEmpresaConfig();" onchange="EMPRESA.representante_legal=this.value.trim();saveEmpresaConfig();"/>
        </div>
      </div>
      <div style="font-size:10px;color:${C.muted};margin-top:8px;">
        💡 La renovación es obligatoria entre el 1 de enero y el 31 de marzo de cada año. Con el último año renovado, Alertas avisa antes de que se venza el plazo.
        ${vencimientoMatricula ? ` Próximo plazo: <b style="color:${C.text};">31 de marzo de ${vencimientoMatricula.anio}</b>${vencimientoMatricula.dias < 0 ? ` <span style="color:${C.red};font-weight:700;">— vencido hace ${Math.abs(vencimientoMatricula.dias)} días</span>` : ` — faltan ${vencimientoMatricula.dias} días`}.` : ""}
      </div>
    </div>

    <!-- ESTADÍSTICAS DE DOCUMENTOS -->
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:11px;margin-bottom:10px;">
      <h3 style="margin:0 0 9px;font-size:12px;color:${C.text};font-weight:700;">📈 ESTADÍSTICAS DE DOCUMENTOS</h3>
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:9px;">
        ${statsHTML}
      </div>
    </div>

    ${esAdmin ? seccionFormatoDoc() : ""}
    ${esAdmin ? seccionBienvenida() : ""}

    <!-- OPCIONES DE ADMINISTRACIÓN -->
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:11px;margin-bottom:10px;">
      <h3 style="margin:0 0 9px;font-size:12px;color:${C.text};font-weight:700;">🛠️ OPCIONES DE ADMINISTRACIÓN</h3>
      
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(300px,1fr));gap:10px;">
        <!-- Opción 1: Ver detalles del almacenamiento -->
        <div style="padding:11px;background:#f8fafc;border:1px solid #2B4A82;border-radius:6px;">
          <div style="font-size:12px;font-weight:700;color:${C.text};margin-bottom:8px;">📦 INFORMACIÓN DE ALMACENAMIENTO</div>
          <div style="font-size:11.5px;color:${C.muted};line-height:1.6;margin-bottom:12px;">
            Los datos viven en la nube (Supabase) y se sincronizan en tiempo real entre dispositivos. El navegador solo guarda una copia local para abrir más rápido.
          </div>
          <button data-action="info-almacenamiento" style="background:#2B4A82;border:none;color:white;padding:8px 16px;border-radius:6px;font-weight:700;cursor:pointer;font-size:11.5px;">
            ℹ️ Más información
          </button>
        </div>

        <!-- Opción 2: Verificar integridad de datos -->
        <div style="padding:11px;background:#f8fafc;border:1px solid #10b981;border-radius:6px;">
          <div style="font-size:12px;font-weight:700;color:${C.text};margin-bottom:8px;">✅ VERIFICAR INTEGRIDAD</div>
          <div style="font-size:11.5px;color:${C.muted};line-height:1.6;margin-bottom:12px;">
            Verifica que todos los documentos estén correctamente guardados y sin corrupción.
          </div>
          <button data-action="verificar-integridad" style="background:#10b981;border:none;color:white;padding:8px 16px;border-radius:6px;font-weight:700;cursor:pointer;font-size:11.5px;">
            🔍 Verificar ahora
          </button>
        </div>

        <!-- Opción 3: Regenerar índices -->
        <div style="padding:11px;background:#fef3c7;border:1px solid #f59e0b;border-radius:6px;">
          <div style="font-size:12px;font-weight:700;color:${C.text};margin-bottom:8px;">🔄 REGENERAR ÍNDICES</div>
          <div style="font-size:11.5px;color:${C.muted};line-height:1.6;margin-bottom:12px;">
            Regenera índices internos si experimentas problemas de rendimiento.
          </div>
          <button data-action="regenerar-indices" style="background:#f59e0b;border:none;color:white;padding:8px 16px;border-radius:6px;font-weight:700;cursor:pointer;font-size:11.5px;">
            ⚡ Regenerar
          </button>
        </div>
      </div>
    </div>

    <!-- ZONA DE PELIGRO -->
    <div style="background:#fef2f2;border:2px solid ${C.red};border-radius:6px;padding:11px;">
      <h3 style="margin:0 0 9px;font-size:14px;color:${C.red};font-weight:700;">🚨 Zona de peligro</h3>
      <div style="display:flex;gap:9px;flex-wrap:wrap;">
        <button data-action="limpiar-todo"
          style="background:#7f1d1d;border:none;color:white;padding:11px 22px;border-radius:6px;font-weight:700;cursor:pointer;font-size:12px;">
          💥 Limpiar todo y reiniciar
        </button>
      </div>
    </div>
  `;
}

export function showRevalidacionModal(tipo, id) {
  const info = DOC_INFO[tipo];
  const doc = state.data[info.key].find(x => x.id === id);
  const estadoLabel = (ESTADOS[tipo] && ESTADOS[tipo][doc.estado]) || doc.estado;
  state.modal = `
    <div data-action="close-modal-bg" style="position:fixed;top:0;right:0;bottom:0;left:0;background:rgba(15,23,42,.55);display:flex;align-items:center;justify-content:center;z-index:1100;padding:11px;">
      <div data-action="prevent-close" style="background:white;border-radius:6px;width:420px;max-width:100%;box-shadow:0 20px 60px rgba(0,0,0,.3);">
        <div style="padding:18px 20px;border-bottom:1px solid ${C.border};">
          <h3 style="margin:0;font-size:12px;color:${C.text};font-weight:700;">🔒 Documento bloqueado</h3>
        </div>
        <div style="padding:11px;">
          <p style="margin:0 0 12px;font-size:12px;color:${C.text};line-height:1.5;">
            <strong>${escapeHTML(doc.numero)}</strong> está en estado <strong>${estadoLabel}</strong>${docTieneHijos(tipo,doc) ? " y <strong>ha generado documentos derivados</strong>" : ""} — no se puede modificar directamente.
          </p>
          <p style="margin:0 0 9px;font-size:11.5px;color:${C.muted};">
            Si continúas, este documento se desbloqueará para edición. Ten en cuenta que los documentos generados a partir de él <strong>podrían quedar inconsistentes</strong> con los cambios que realices.
          </p>
          <div style="display:flex;gap:10px;justify-content:flex-end;">
            <button data-action="close-modal" style="${btnStyle(null,"secondary")}">Cancelar</button>
            <button data-action="revalidar-edicion" data-tipo="${tipo}" data-id="${id}" style="${btnStyle(C.amber)}">🔓 Desbloquear y editar</button>
          </div>
        </div>
      </div>
    </div>
  `;
  render();
}
